import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";


import { normalizeCategory } from "@/utils/categoryUtils";
import { normalizeColor, getCssColor, colorMap } from "@/utils/colorUtils";
import { sortBrands } from "@/utils/sortUtils";

export default function FilterPanel({ filters, setFilters, onClose, products = [], availableOptions = null, hideBrands = false }) {


    // Dynamic Category Mapping
    const mapCategory = React.useCallback((rawCat) => {
        if (!rawCat) return null;

        // Use Backend Rules if available
        if (availableOptions && availableOptions.categoryRules && availableOptions.categoryRules.length > 0) {
            const lowerCat = rawCat.toLowerCase();
            for (const rule of availableOptions.categoryRules) {
                // Rule keywords check
                if (rule.keywords && Array.isArray(rule.keywords)) {
                    for (const k of rule.keywords) {
                        if (lowerCat.includes(k.toLowerCase())) {
                            return rule.name;
                        }
                    }
                }
            }
            // Strict Mode: If rules exist but no match, return NULL (don't show "Knit" if not in rules)
            return null;
        }

        // Use standard static helper if rules missing
        return normalizeCategory(rawCat);
    }, [availableOptions]);

    // Get unique values from ALL available products (via metadata) + loaded products
    const subcategoriesMap = React.useMemo(() => {
        const counts = {};

        // 1. Seed from backend metadata styles (covers ALL products in DB, not just loaded batch)
        if (availableOptions && availableOptions.styles) {
            availableOptions.styles.forEach(rawStyle => {
                const normalized = mapCategory(rawStyle);
                if (normalized) {
                    // Initialize to 0 — exact counts come from loaded products below
                    if (!(normalized in counts)) counts[normalized] = 0;
                }
            });
        }

        // 2. Aggregate counts from loaded products (for display accuracy)
        if (products) {
            products.forEach(p => {
                const catsToCheck = [];
                if (p.subcategory) catsToCheck.push(p.subcategory);
                if (p.style) catsToCheck.push(p.style);

                catsToCheck.forEach(rawCat => {
                    const normalized = mapCategory(rawCat);
                    if (normalized) {
                        counts[normalized] = (counts[normalized] || 0) + 1;
                    }
                });
            });
        }

        return counts;
    }, [products, availableOptions, mapCategory]);

    const subcategories = React.useMemo(() => {
        // Sort by rule order if available
        const keys = Object.keys(subcategoriesMap);

        if (availableOptions && availableOptions.categoryRules) {
            return keys.sort((a, b) => {
                const indexA = availableOptions.categoryRules.findIndex(r => r.name === a);
                const indexB = availableOptions.categoryRules.findIndex(r => r.name === b);

                // If both found in rules, sort by rule order (priority)
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;

                // If one found, it comes first
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;

                // Alphabetical otherwise
                return a.localeCompare(b);
            });
        }

        return keys.sort();
    }, [subcategoriesMap, availableOptions]);

    const normalizedColors = React.useMemo(() => {
        const allRawColors = [];

        if (availableOptions && availableOptions.colors) {
            allRawColors.push(...availableOptions.colors);
        }

        if (products) {
            products.forEach(p => {
                if (p.colors && Array.isArray(p.colors)) {
                    p.colors.forEach(c => {
                        const cName = typeof c === 'object' ? c.color : c;
                        if (cName) allRawColors.push(cName);
                    });
                }
            });
        }

        const uniqueColors = new Set();
        allRawColors.forEach(c => {
            const normalized = normalizeColor(c);
            if (normalized) uniqueColors.add(normalized);
        });

        if (uniqueColors.size === 0) {
            // Fallback to showing all standard colors if empty
            return Object.keys(colorMap);
        }
        return [...uniqueColors];
    }, [products, availableOptions]);

    // Calculate dynamic price range
    const { globalMinPrice, globalMaxPrice } = React.useMemo(() => {
        if (availableOptions && availableOptions.maxPrice) {
            return { globalMinPrice: 0, globalMaxPrice: availableOptions.maxPrice };
        }
        if (!products || products.length === 0) return { globalMinPrice: 0, globalMaxPrice: 500 };
        let min = Infinity;
        let max = -Infinity;
        products.forEach(p => {
            // Handle price as number or string
            const pVal = typeof p.price === 'string' ? parseFloat(p.price) : p.price;
            const price = Number.isNaN(pVal) ? 0 : pVal;
            if (price < min) min = price;
            if (price > max) max = price;
        });
        // Safety check if min/max are still infinity (e.g. all prices invalid)
        if (min === Infinity) min = 0;
        if (max === -Infinity) max = 500;

        return {
            globalMinPrice: Math.floor(min),
            globalMaxPrice: Math.ceil(max / 100) * 100
        };
    }, [products, availableOptions]);

    // Chromatic sort order
    const colorOrder = [
        'blanc', 'beige', 'jaune', 'orange', 'rouge', 'rose', 'violet', 'bleu', 'vert', 'marron', 'gris', 'noir', 'multicolore'
    ];

    const colors = normalizedColors.sort((a, b) => {
        const indexA = colorOrder.indexOf(a);
        const indexB = colorOrder.indexOf(b);
        // Put unknown colors at the end
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
    });
    const brands = React.useMemo(() => {
        if (availableOptions && availableOptions.brands) {
            return sortBrands(availableOptions.brands);
        }
        return sortBrands([...new Set(products.map(p => p.brand).filter(Boolean))]);
    }, [products, availableOptions]);
    const sizes = ["XS", "S", "M", "L", "XL", "XXL"];

    const toggleArrayFilter = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: (prev[key] || []).includes(value)
                ? prev[key].filter(v => v !== value)
                : [...(prev[key] || []), value]
        }));
    };

    return (
        <Card className="w-full max-w-md bg-white/95 dark:bg-[hsl(222,20%,10%)]/95 dark:border-[hsl(222,16%,16%)] backdrop-blur-sm shadow-xl">
            <CardHeader className="border-b flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-xl">Filtres</CardTitle>
                <div
                    onClick={onClose}
                    className="cursor-pointer p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Prix */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Prix (€)</Label>
                    <div className="px-2 pt-2">
                        <Slider
                            value={[Math.max(filters.maxPrice || globalMaxPrice, globalMinPrice)]}
                            onValueChange={([max]) => setFilters(prev => ({ ...prev, maxPrice: max }))}
                            min={globalMinPrice}
                            max={Math.max(globalMaxPrice, 100)} // Ensure at least some range
                            step={5}
                            className="w-full py-4"
                        />
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 font-medium">
                        <span>Min: {globalMinPrice}€</span>
                        <span>Max: {filters.maxPrice || globalMaxPrice}€</span>
                    </div>
                </div>

                {/* Sous-catégories */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Type de vêtement</Label>
                    <div className="flex flex-wrap gap-2">
                        {subcategories.map(subcategory => (
                            <Badge
                                key={subcategory}
                                variant={filters.subcategories.includes(subcategory) ? "default" : "outline"}
                                className={`cursor-pointer transition-colors ${filters.subcategories.includes(subcategory)
                                    ? "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm"
                                    : "hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-200"
                                    }`}
                                onClick={() => toggleArrayFilter("subcategories", subcategory)}
                            >
                                {subcategory}
                            </Badge>
                        ))}
                    </div>
                </div>

                {/* Couleurs */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Couleurs</Label>
                    <div className="flex flex-wrap gap-2">
                        {colors.map(colorKey => {
                            const isSelected = filters.colors?.includes(colorKey);
                            return (
                                <div
                                    key={colorKey}
                                    className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-all ${isSelected
                                        ? "ring-2 ring-offset-2 ring-amber-500 border-transparent scale-110"
                                        : "border border-gray-200 hover:scale-105"
                                        }`}
                                    style={{
                                        background: colorMap[colorKey] || colorKey
                                    }}
                                    onClick={() => toggleArrayFilter("colors", colorKey)}
                                    title={colorKey.charAt(0).toUpperCase() + colorKey.slice(1)}
                                >
                                    {isSelected && (
                                        <span className={`text-xs font-bold ${['blanc', 'white', 'jaune', 'beige', 'crème', 'écru'].includes(colorKey) ? 'text-black' : 'text-white'}`}>
                                            ✓
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Marques */}
                {!hideBrands && (
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Marques</Label>
                        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                            {brands.map(brand => (
                                <Badge
                                    key={brand}
                                    variant={filters.brands?.includes(brand) ? "default" : "outline"}
                                    className={`cursor-pointer transition-colors ${filters.brands?.includes(brand)
                                    ? "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm"
                                    : "hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-200"
                                    }`}
                                    onClick={() => toggleArrayFilter("brands", brand)}
                                >
                                    {brand}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tailles */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Tailles</Label>
                    <div className="flex flex-wrap gap-2">
                        {sizes.map(size => (
                            <Badge
                                key={size}
                                variant={filters.sizes.includes(size) ? "default" : "outline"}
                                className={`cursor-pointer transition-colors ${filters.sizes.includes(size)
                                    ? "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm"
                                    : "hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-200"
                                    }`}
                                onClick={() => toggleArrayFilter("sizes", size)}
                            >
                                {size}
                            </Badge>
                        ))}
                    </div>
                </div>

                {/* Stock */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Disponibilité</Label>
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="inStock"
                            checked={filters.inStockOnly}
                            onCheckedChange={(checked) => setFilters(prev => ({ ...prev, inStockOnly: checked }))}
                        />
                        <label
                            htmlFor="inStock"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            En stock uniquement
                        </label>
                    </div>
                </div>

                {/* Nouveautés */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Nouveautés</Label>
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="isNew"
                            checked={filters.is_new}
                            onCheckedChange={(checked) => setFilters(prev => ({ ...prev, is_new: checked }))}
                        />
                        <label
                            htmlFor="isNew"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            Afficher uniquement les nouveautés
                        </label>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}